import React, { useState, useEffect } from "react";
import { DOCUMENT_DROPZONE_ACCEPT } from "shared/constants/officeFiles";
import styles from "./style.module.scss";
import ImageItemsList, {
  ImageItem,
} from "shared/components/common/imageItemsList";

interface StepTwoProps {
  data: any;
  updateData: (data: any) => void;
  errors: Record<string, string>;
  onNext?: () => void;
  onPrev?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

const StepTwo = ({ data, updateData, errors }: StepTwoProps) => {
  const getInitialImageItems = (): ImageItem[] => {
    if (
      data?.imageItems &&
      Array.isArray(data.imageItems) &&
      data.imageItems.length > 0
    ) {
      return data.imageItems.map((item: any) => ({
        id: item.id || Date.now().toString(),
        imageUrl: item.imageUrl || item.image,
        imageLabel: item.imageLabel || "",
        notes: item.notes || "",
        file: item.file || null,
        hasImage: !!(item.imageUrl || item.image || item.file),
      }));
    }
    return [
      {
        id: "1",
        imageUrl: undefined,
        imageLabel: "",
        notes: "",
        hasImage: false,
      },
    ];
  };

  const [imageItems, setImageItems] = useState<ImageItem[]>(
    getInitialImageItems(),
  );
  const isInitialMount = React.useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (
      data?.imageItems &&
      Array.isArray(data.imageItems) &&
      data.imageItems.length > 0
    ) {
      const restoredItems = data.imageItems.map((item: any) => ({
        id: item.id || Date.now().toString(),
        imageUrl: item.imageUrl || item.image,
        imageLabel: item.imageLabel || "",
        notes: item.notes || "",
        file: item.file || null,
        hasImage: !!(item.imageUrl || item.image || item.file),
      }));

      const currentIds = imageItems
        .map((item: ImageItem) => item.id)
        .sort()
        .join(",");
      const restoredIds = restoredItems
        .map((item: ImageItem) => item.id)
        .sort()
        .join(",");

      if (currentIds !== restoredIds) {
        setImageItems(restoredItems);
      }
    }
  }, [data?.imageItems]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    updateData({
      imageItems,

      imageLabel: imageItems[0]?.imageLabel || "",
      notes: imageItems[0]?.notes || "",
    });
  }, [imageItems, updateData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditorChange = (id: string, value: string) => {
    setImageItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, notes: value } : item,
      ),
    );
  };

  const handleAddMore = () => {
    const newId = (imageItems.length + 1).toString();
    setImageItems((prevItems) => [
      ...prevItems,
      {
        id: newId,
        imageLabel: "",
        notes: "",
        hasImage: false,
      },
    ]);
  };

  const handleDelete = (id: string) => {
    if (imageItems.length > 1) {
      setImageItems((prevItems) => prevItems.filter((item) => item.id !== id));
    }
  };

  const handleFileChange = (
    id: string,
    file: File | null,
    preview: string | null,
  ) => {
    setImageItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id
          ? {
              ...item,
              file,
              imageUrl: preview || undefined,
              hasImage: !!file,
            }
          : item,
      ),
    );
  };

  return (
    <div className={styles.big}>
      <div className={`mt-8 ${styles.cardWrapper}`}>
        {errors?.imageItems && (
          <div className={styles.fieldError}>{errors.imageItems}</div>
        )}
        <ImageItemsList
          imageItems={imageItems}
          errors={errors}
          onFileChange={handleFileChange}
          onImageLabelChange={(id, value) =>
            setImageItems((prev) =>
              prev.map((img) =>
                img.id === id ? { ...img, imageLabel: value } : img,
              ),
            )
          }
          onNotesChange={handleEditorChange}
          onAddMore={handleAddMore}
          onDelete={handleDelete}
          notesRequired={false}
          accept={DOCUMENT_DROPZONE_ACCEPT}
        />
      </div>
    </div>
  );
};

export default StepTwo;
